import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../config.js';
import { toApiError } from '../lib/api-error';

/**
 * Creates a shell teardown workflow and links it to the given seed workflow.
 * After success, invalidates the workflows query so the UI shows the teardown toggle.
 */
export function useAddTeardown(seedWorkflowId: string, seedWorkflowName: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      // Step 1: Create a shell teardown workflow
      const teardownRes = await fetch(`${API_URL}/workflows`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name: `Teardown: ${seedWorkflowName}`,
          description: `Cleans up seed data from "${seedWorkflowName}"`,
          status: 'draft',
          flowType: 'teardown',
        }),
      });
      if (!teardownRes.ok) throw await toApiError(teardownRes, 'Failed to create teardown workflow');
      const { workflow: teardownWf } = await teardownRes.json();

      // Step 2: Link teardown to the seed workflow
      const linkRes = await fetch(`${API_URL}/workflows/${seedWorkflowId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ teardownWorkflowId: teardownWf.id }),
      });
      if (!linkRes.ok) throw await toApiError(linkRes, 'Failed to link teardown workflow');

      return teardownWf;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflows'] });
    },
  });
}
